import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Tooltip } from '@mui/material';
import { array, boolean, object, string } from 'yup';
import { useFormik } from 'formik';
import { AcademicRefereeConfirmBox } from './AcademicRefereeConfirmBox';
import { type RefereeConfirmation } from './types';
import { PAGE_TEXT } from '../../../../lang';
import {
  useConfirmAcademicReferee,
  useGetAcademicReferee,
} from '../../../../graphql';
import {
  Loading,
  Alert,
  type AlertDetails,
  Accordion,
  ButtonContainer,
  Button,
  Checkbox,
} from '../../../../../../components';
import { APPLICATION_TEXT } from '../../../../../Monitor/components/lang';

export enum RefereeTickMode {
  DirectorConfirm = 'DirectorConfirm',
  DirectorPolish = 'DirectorPolish',
  ViewMode = 'ViewMode',
}

type RefereeTickBoxPermission = {
  viewable: boolean;
  editable: boolean;
};
const refereeTickPermissionConfig: Record<
  RefereeTickMode,
  {
    directorConfirmBox: RefereeTickBoxPermission;
    directorPolishBox: RefereeTickBoxPermission;
    enableJumpNext: boolean;
  }
> = {
  [RefereeTickMode.DirectorConfirm]: {
    directorConfirmBox: {
      viewable: true,
      editable: true,
    },
    directorPolishBox: {
      viewable: false,
      editable: false,
    },
    enableJumpNext: true,
  },
  [RefereeTickMode.DirectorPolish]: {
    directorConfirmBox: {
      viewable: false,
      editable: false,
    },
    directorPolishBox: {
      viewable: true,
      editable: true,
    },
    enableJumpNext: false,
  },
  [RefereeTickMode.ViewMode]: {
    directorConfirmBox: {
      viewable: true,
      editable: false,
    },
    directorPolishBox: {
      viewable: true,
      editable: false,
    },
    enableJumpNext: false,
  },
};

export const AcademicRefereeConfirmation: React.FC<{
  refereeTickMode: RefereeTickMode;
  refetchAssessment: () => void;
  onJumpToNext?: () => void;
}> = props => {
  const { refereeTickMode, refetchAssessment, onJumpToNext = () => {} } = props;
  const { applicationId = '' } = useParams();
  const {
    academicReferees,
    confirmations,
    loading,
    error: refereeConfirmationError,
    refetch: refereeConfirmationRefetch,
  } = useGetAcademicReferee(applicationId);

  const { directorConfirmBox, directorPolishBox, enableJumpNext } =
    refereeTickPermissionConfig[refereeTickMode];
  const [saveLoading, setSaveLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'success',
  });

  const confirmAcademicReferee = useConfirmAcademicReferee();
  const handleSaveConfirmation = async (
    refereeConfirmation: RefereeConfirmation[],
  ): Promise<void> => {
    setSaveLoading(true);

    try {
      const variables = {
        applicationId,
        input: refereeConfirmation,
      };
      await confirmAcademicReferee({
        variables,
      });
      setAlertDetails({
        alertContent:
          refereeTickMode === RefereeTickMode.DirectorConfirm
            ? PAGE_TEXT.DRC_ASSESSMENT_DETAILS.REFEREE_CONFIRM.SUCCESS
            : PAGE_TEXT.DRC_ASSESSMENT_DETAILS.REFEREE_CONFIRM.HKPFS_CONFIRM,
        alertType: 'success',
      });
      refetchAssessment();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Error Occurs. ${(error as Error).message}`,
        alertType: 'error',
      });
    } finally {
      setOpenAlert(true);
      setSaveLoading(false);
      refereeConfirmationRefetch();
    }
  };

  const formik = useFormik<RefereeConfirmation[]>({
    initialValues: confirmations,
    validationSchema: array().of(
      object({
        valid: boolean().required(),
        academicRefereeId: string().required(),
        selectedToHkpfs: boolean().required(),
      }),
    ),
    enableReinitialize: true,
    onSubmit: async value => {
      if (refereeTickMode === RefereeTickMode.DirectorPolish) {
        const selectedCount = value.filter(item => item.selectedToHkpfs).length;
        if (selectedCount < 2) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setAlertDetails({
            alertContent:
              'Please select the two most appropriate referee reports for submission.',
            alertType: 'warning',
          });
          setOpenAlert(true);
          return;
        }
      }

      await handleSaveConfirmation(value);
    },
  });

  useEffect(() => {
    if (!loading) {
      formik.resetForm({
        values: {
          ...confirmations,
        },
      });
    }
  }, [loading]);

  if (refereeConfirmationError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs. ` + (refereeConfirmationError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const enableSave = directorConfirmBox.editable || directorPolishBox.editable;
  return (
    <div className="w-full">
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <Accordion
          className="w-full"
          defaultExpanded
          disableExpand
          title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.REFEREE_CONFIRM.TITLE}
          details={
            <Box padding={'0.75em'}>
              {academicReferees?.length > 0 ? (
                <form onSubmit={formik.handleSubmit} noValidate>
                  <div className="pt-2">
                    {academicReferees?.map((academicReferee, index: number) => (
                      <div key={index}>
                        <AcademicRefereeConfirmBox
                          key={index}
                          titleArea={
                            <div
                              className="flex flex-row items-center w-full gap-4"
                              key={index}
                            >
                              <p className="text-left text-base font-medium">{`${APPLICATION_TEXT.ACADEMIC_REFEREE.SUBTITLE} - ${index + 1}`}</p>
                              {directorPolishBox.viewable && (
                                <Tooltip title="Selected for HKPFS">
                                  <span>
                                    <Checkbox
                                      disabled={!directorPolishBox.editable}
                                      name={`[${index}].selectedToHkpfs`}
                                      label={''}
                                      value={
                                        formik.values[index]?.selectedToHkpfs ??
                                        false
                                      }
                                      onChange={formik.handleChange}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          }
                          academicReferee={academicReferee}
                        />
                        {directorConfirmBox.viewable && (
                          <Checkbox
                            disabled={!directorConfirmBox.editable}
                            className="!mx-1 mb-8"
                            name={`[${index}].valid`}
                            label={
                              PAGE_TEXT.DRC_ASSESSMENT_DETAILS.REFEREE_CONFIRM
                                .CHECKBOX_LABEL
                            }
                            value={formik.values[index]?.valid ?? false}
                            onChange={formik.handleChange}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {enableSave && (
                    <ButtonContainer
                      spacing={4}
                      className="my-4 justify-center"
                    >
                      <Button variant="contained" color="primary" type="submit">
                        {PAGE_TEXT.SAVE}
                      </Button>

                      {enableJumpNext && (
                        <Button onClick={onJumpToNext}>{PAGE_TEXT.NEXT}</Button>
                      )}
                    </ButtonContainer>
                  )}
                </form>
              ) : (
                <div className="m-6">
                  {PAGE_TEXT.DRC_ASSESSMENT_DETAILS.REFEREE_CONFIRM.NA}
                </div>
              )}
            </Box>
          }
        />
      )}
      {saveLoading && <Loading />}
    </div>
  );
};
